/**
 * External messaging tenant linking.
 *
 * The proxy owns the transactional work for this flow: validate the HMAC key,
 * create the write-only secret scoped to the authenticated organization, create
 * the external_messaging_tenants row, and return `already_connected` for an
 * existing tenant instead of failing the UI flow.
 */

import { createApi } from '@reduxjs/toolkit/query/react'

import { PROXY_API_V1_URL, ontologyBaseQuery } from '../core'
import type {
  ConnectExternalMessagingTenantPayload,
  ConnectExternalMessagingTenantResponse,
  ExternalMessagingChannel,
  ExternalMessagingChannelListFilters,
  ExternalMessagingTenant,
  MessagingPlatform,
  UpdateExternalMessagingChannelPayload
} from './types'

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

export const EXTERNAL_MESSAGING_TAG_TYPES = [
  'ExternalMessagingTenant',
  'ExternalMessagingChannel'
] as const
export type ExternalMessagingTagType = (typeof EXTERNAL_MESSAGING_TAG_TYPES)[number]

export const externalMessagingApi = createApi({
  reducerPath: 'externalMessagingApi',
  baseQuery: ontologyBaseQuery,
  refetchOnReconnect: true,
  tagTypes: EXTERNAL_MESSAGING_TAG_TYPES,
  endpoints: (builder) => ({
    /**
     * Look up the external messaging tenant by its platform-side tenant ID
     * (Teams tenant ID / Slack workspace ID). Resolves to the tenant when it
     * is already whitelisted and fails with a 404 when it is not — the connect
     * landing page uses this to skip the permission prompt for an
     * already-connected tenant and send the user straight to Integrations.
     */
    getExternalMessagingTenant: builder.query<
      ExternalMessagingTenant,
      { external_tenant_id: string }
    >({
      query: ({ external_tenant_id }) => ({
        url: `${PROXY_API_V1_URL}/external-messaging/tenants/${encodeURIComponent(external_tenant_id)}`
      }),
      providesTags: (result) =>
        result
          ? [
              {
                type: 'ExternalMessagingTenant' as const,
                id: `${result.platform}:${result.external_tenant_id}`
              }
            ]
          : []
    }),
    listExternalMessagingChannels: builder.query<
      ExternalMessagingChannel[],
      ExternalMessagingChannelListFilters | void
    >({
      query: (filters) => ({
        url: `${PROXY_API_V1_URL}/external-messaging/channels`,
        params: (filters ?? undefined) as Record<string, string | number | undefined>
      }),
      transformResponse: (
        response: {
          channels: ExternalMessagingChannel[]
          next_page_token?: string
          total_size?: number
        } | null
      ) => asArray<ExternalMessagingChannel>(response?.channels),
      providesTags: (result) =>
        result
          ? [
              ...result.map((channel) => ({
                type: 'ExternalMessagingChannel' as const,
                id: channel.id
              })),
              { type: 'ExternalMessagingChannel' as const, id: 'LIST' }
            ]
          : [{ type: 'ExternalMessagingChannel' as const, id: 'LIST' }]
    }),
    updateExternalMessagingChannel: builder.mutation<
      ExternalMessagingChannel,
      { id: string; patch: UpdateExternalMessagingChannelPayload }
    >({
      query: ({ id, patch }) => ({
        url: `${PROXY_API_V1_URL}/external-messaging/channels/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body: patch
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ExternalMessagingChannel', id },
        { type: 'ExternalMessagingChannel', id: 'LIST' }
      ]
    }),
    connectExternalMessagingTenant: builder.mutation<
      ConnectExternalMessagingTenantResponse,
      ConnectExternalMessagingTenantPayload
    >({
      query: (body) => ({
        url: `${PROXY_API_V1_URL}/external-messaging/tenants/connect`,
        method: 'POST',
        body
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ExternalMessagingTenant', id: `${arg.platform}:${arg.external_tenant_id}` },
        { type: 'ExternalMessagingTenant', id: 'LIST' }
      ]
    }),
    /**
     * De-list (disconnect) a whitelisted external messaging tenant. The proxy
     * scopes the delete to the caller's own organization and cascades the
     * tenant's channels, conversation history, delivery records, and signing
     * secret. Channels are invalidated too because the tenant's rows disappear.
     */
    deleteExternalMessagingTenant: builder.mutation<
      void,
      { external_tenant_id: string; platform: MessagingPlatform }
    >({
      query: ({ external_tenant_id }) => ({
        url: `${PROXY_API_V1_URL}/external-messaging/tenants/${encodeURIComponent(external_tenant_id)}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ExternalMessagingTenant', id: `${arg.platform}:${arg.external_tenant_id}` },
        { type: 'ExternalMessagingTenant', id: 'LIST' },
        { type: 'ExternalMessagingChannel', id: 'LIST' }
      ]
    })
  })
})

export const {
  useGetExternalMessagingTenantQuery,
  useListExternalMessagingChannelsQuery,
  useUpdateExternalMessagingChannelMutation,
  useConnectExternalMessagingTenantMutation,
  useDeleteExternalMessagingTenantMutation
} = externalMessagingApi
